/* eslint-disable no-console */
import zoomSdk from '@zoom/appssdk'
import cookie, { AUTH_PENDING_COOKIE } from '@/services/cookie'
import router from '@/router'
import util from '@/util'
import store from '@/vuex/store'
import sentry from '@/services/sentry'

const APPLICATION_JSON = 'application/json'

const SET_ZOOM_MEETING_ID = 'SET_ZOOM_MEETING_ID'
const SET_ZOOM_RUNNING_CONTEXT = 'SET_ZOOM_RUNNING_CONTEXT'
const SET_ZOOM_USER_CONTEXT = 'SET_ZOOM_USER_CONTEXT'
const SET_ZOOM_CONFIG = 'SET_ZOOM_CONFIG'
const SET_ZOOM_READY = 'SET_ZOOM_READY'
const SET_SHARING_SCREEN = 'SET_SHARING_SCREEN'

const _state = {
  zoomMeetingId: null,
  zoomRunningContext: null,
  zoomUserContext: null,
  zoomConfig: null,
  isZoomReady: sessionStorage.getItem('zoomApp') == 'true',
  isSharingScreen: false,
}

const ZOOM_USER_ID_COOKIE_KEY = 'zuid'
const ZOOM_MEETING_ID_COOKIE_KEY = 'zmid'

const ZoomAppConfigs = {
  version: '0.16.17',
  capabilities: ['stopShareScreen', 'getUserContext', 'getMeetingUUID', 'onParticipantChange', 'sendAppInvitation', 'sendAppInvitationToAllParticipants', 'shareApp', 'launchAppInMeeting', 'openUrl', 'getRunningContext', 'authorize', 'onAuthorized', 'onShareApp', 'onShareScreen', 'appPopout', 'onAppPopout', 'onCloseAppForParticipants', 'closeApp'],
  popoutSize: {
    width: 800,
    height: 600,
  },
}

const getters = {
  zoomMeetingId(state) {
    return state.zoomMeetingId
  },
  zoomRunningContext(state) {
    return state.zoomRunningContext
  },
  zoomUserContext(state) {
    return state.zoomUserContext
  },
  zoomConfig(state) {
    return state.zoomConfig
  },
  isZoomReady(state) {
    return state.isZoomReady
  },
}

function getDeeplinkRedirectUrl(zoomSessionId) {
  return encodeURIComponent(`${window.location.origin}/api/zoomapp/deeplink?zid=${zoomSessionId}`)
}

function setZoomSessionId(zoomSessionId) {
  if (!zoomSessionId) return
  const ZOOM_SESSION_TIMEOUT_DAYS = 1

  cookie.setValue(ZOOM_USER_ID_COOKIE_KEY, zoomSessionId, {
    expires: ZOOM_SESSION_TIMEOUT_DAYS,
  })
}

function getZoomSessionId() {
  return cookie.getValue(ZOOM_USER_ID_COOKIE_KEY)
}

function handleOnAuthorizedEvent(event) {
  store.dispatch('zoomApp/handleOnAuthorizedEventAction', event)
}

const actions = {
  async configZoomSdkAction({ commit, dispatch }) {
    const zoomConfig = await zoomSdk.config(ZoomAppConfigs)
    commit(SET_ZOOM_CONFIG, zoomConfig)
    dispatch('getZoomRunningContextAction')
    commit(SET_ZOOM_READY, true)
  },

  async authorizeAction() {
    try {
      const authorizeOptions = await fetch('/api/zoomapp/authorize').then((r) => r.json())
      await zoomSdk.authorize(authorizeOptions)
      setZoomSessionId(authorizeOptions.zoomSessionId)
      return authorizeOptions
    } catch (e) {
      sentry.captureException(e)
      return null
    }
  },

  async handleOnAuthorizedEventAction(_, event) {
    const { code, state, redirectUri } = event
    const zoomSessionId = getZoomSessionId()
    const authUrlWithChallenge = `${window.location.origin}${router.resolve(`/pages/login?zoomapp=true&redirect=${getDeeplinkRedirectUrl(zoomSessionId)}`).href}`

    const onAuthorizedParams = {
      code,
      state,
      zid: zoomSessionId,
      redirectUri: encodeURIComponent(redirectUri),
    }
    fetch(`${window.location.origin}/api/zoomapp/onauthorized`, {
      method: 'POST',
      headers: { 'Content-Type': APPLICATION_JSON },
      body: JSON.stringify(onAuthorizedParams),
    })
      .then((res) => res.json())
      .then((data) => {
        zoomSdk.openUrl({ url: authUrlWithChallenge })
        zoomSdk.removeEventListener('onAuthorized', handleOnAuthorizedEvent)

        return data
      }).catch((e) => {
        sentry.captureException(e)
      })
  },

  async openLoginPageAction({ dispatch }) {
    cookie.setValue(AUTH_PENDING_COOKIE, 'true')
    zoomSdk.addEventListener('onAuthorized', handleOnAuthorizedEvent)
    await dispatch('authorizeAction')
  },

  async checkZoomAppAction({ state }) {
    const isNotLoggedIn = !util.isLoggedIn()
    if (isNotLoggedIn && state.isZoomReady) {
      router.replace({
        name: 'ZoomApp',
      })
    }
  },

  async getZoomMeetingIdAction({ commit }) {
    try {
      const response = await zoomSdk.getMeetingUUID()
      commit(SET_ZOOM_MEETING_ID, response.meetingUUID)
    } catch (e) {
      console.log(e)
    }
  },
  async getZoomRunningContextAction({ commit }) {
    try {
      const response = await zoomSdk.getRunningContext()
      commit(SET_ZOOM_RUNNING_CONTEXT, response.context)
    } catch (e) {
      sentry.captureException(e)
    }
  },
  async getZoomUserContextAction({ commit }) {
    try {
      const response = await zoomSdk.getUserContext()
      commit(SET_ZOOM_USER_CONTEXT, response)
    } catch (e) {
      sentry.captureException(e)
    }
  },
  async sendAppInvitationToAllParticipantAction() {
    try {
      await zoomSdk.sendAppInvitationToAllParticipants()
    } catch (e) {
      sentry.captureException(e)
    }
  },

  async sendAppInvitationAction(_, participantUUIDs) {
    try {
      await zoomSdk.sendAppInvitation({
        participantUUIDs,
      })
    } catch (e) {
      sentry.captureException(e)
    }
  },
  async shareAppToParticipantsAction({ dispatch, commit, state }) {
    try {
      zoomSdk.onShareApp((event) => {
        if (event === 'start') {
          dispatch('sendAppInvitationToAllParticipantAction')
          commit(SET_SHARING_SCREEN, true)
        }
        if (event === 'stop') {
          commit(SET_SHARING_SCREEN, false)
        }
      })
      zoomSdk.onParticipantChange((event) => {
        if (!event.participants || event.participants.length === 0) {
          return
        }
        const participantsJoining = event.participants.filter((p) => p.status == 'join')
        if (participantsJoining.length == 0) {
          return
        }

        const participantUUIDs = participantsJoining.map((p) => p.participantUUID)

        dispatch('sendAppInvitationAction', participantUUIDs)
      })
      if (!state.isSharingScreen) {
        await zoomSdk.shareApp({ action: 'start', withSound: true })
      } else {
        dispatch('sendAppInvitationToAllParticipantAction')
      }
    } catch (e) {
      sentry.captureException(e)
    }
  },

  async attachPresentationToZoomMeetingAction({ dispatch }, presentation) {
    try {
      const zoomMeetingId = cookie.getValue(ZOOM_MEETING_ID_COOKIE_KEY)
      const ahaUniqueAccessCode = presentation.uniqueAccessCode
      await fetch('/api/zoomapp/presentation/attach', {
        method: 'POST',
        headers: {
          'Content-Type': APPLICATION_JSON,
        },
        body: JSON.stringify({
          zoomMeetingId,
          ahaUniqueAccessCode,
        }),
      })

      dispatch('setPresentFromBeginningAction', presentation, { root: true })
    } catch (e) {
      sentry.captureException(e)
    }
  },
  async detachPresentationToZoomMeetingAction() {
    try {
      const zoomMeetingId = cookie.getValue(ZOOM_MEETING_ID_COOKIE_KEY)
      await fetch('/api/zoomapp/presentation/detach', {
        method: 'DELETE',
        headers: {
          'Content-Type': APPLICATION_JSON,
        },
        body: JSON.stringify({
          zoomMeetingId,
        }),
      })
    } catch (e) {
      sentry.captureException(e)
    }
  },

  backToZoomStarterPageAction() {
    router.replace({
      name: 'ZoomApp',
    })
  },

  downloadFileAction(_, link) {
    zoomSdk.openUrl({ url: link })
  },
}

const mutations = {
  [SET_ZOOM_MEETING_ID](state, zoomMeetingId) {
    state.zoomMeetingId = zoomMeetingId
  },
  [SET_ZOOM_RUNNING_CONTEXT](state, zoomRunningContext) {
    state.zoomRunningContext = zoomRunningContext
  },
  [SET_ZOOM_USER_CONTEXT](state, zoomUserContext) {
    state.zoomUserContext = zoomUserContext
  },
  [SET_ZOOM_CONFIG](state, zoomConfig) {
    state.zoomConfig = zoomConfig
  },
  [SET_ZOOM_READY](state, isZoomReady) {
    state.isZoomReady = isZoomReady
    sessionStorage.setItem('zoomApp', 'true')
  },
  [SET_SHARING_SCREEN](state, isSharingScreen) {
    state.isSharingScreen = isSharingScreen
  },
}

const zoomApp = {
  namespaced: true,
  state: _state,
  getters,
  actions,
  mutations,
}

export default zoomApp
